/**
 * Income sources — where money comes from (plan step 1.1).
 *
 * Creating one is a plain insert: a database trigger mirrors it into an income ledger
 * account, so the source is postable the moment it exists. Like accounts, there is no
 * DELETE grant — a source is archived and its history keeps pointing at it.
 */
import type { IncomeSourceType, StabilityClass } from '@pfo/domain';

import { supabase } from '../../../shared/api/supabase/client.js';
import { appErrorException } from '../../../shared/lib/errors.js';
import { fetchPreferences } from '../../preferences/data/preferencesRepository.js';

export type IncomeSource = Readonly<{
  id: string;
  name: string;
  sourceType: IncomeSourceType;
  stabilityClass: StabilityClass;
  defaultDestinationAccountId: string | null;
  isActive: boolean;
  isFavorite: boolean;
}>;

export type NewIncomeSource = Readonly<{
  name: string;
  sourceType: IncomeSourceType;
  stabilityClass: StabilityClass;
  defaultDestinationAccountId?: string;
}>;

export async function fetchIncomeSources(): Promise<readonly IncomeSource[]> {
  const [sourcesResult, preferences] = await Promise.all([
    supabase
      .from('income_sources')
      .select('id, name, source_type, stability_class, default_destination_account_id, is_active')
      .order('name'),
    fetchPreferences(),
  ]);
  if (sourcesResult.error) throw appErrorException(sourcesResult.error);

  const favorites = new Set(preferences.favoriteIncomeSourceIds);

  return (sourcesResult.data ?? []).map((row) => ({
    id: row.id,
    name: row.name,
    sourceType: row.source_type as IncomeSourceType,
    stabilityClass: row.stability_class as StabilityClass,
    defaultDestinationAccountId: row.default_destination_account_id,
    isActive: row.is_active,
    isFavorite: favorites.has(row.id),
  }));
}

export async function createIncomeSource(
  context: { householdId: string; subjectId: string },
  input: NewIncomeSource,
): Promise<string> {
  const { data, error } = await supabase
    .from('income_sources')
    .insert({
      household_id: context.householdId,
      owner_subject_id: context.subjectId,
      name: input.name.trim(),
      source_type: input.sourceType,
      stability_class: input.stabilityClass,
      ...(input.defaultDestinationAccountId === undefined ||
      input.defaultDestinationAccountId === ''
        ? {}
        : { default_destination_account_id: input.defaultDestinationAccountId }),
    })
    .select('id')
    .single();
  if (error) throw appErrorException(error);
  return data.id;
}

export type IncomeSourcePatch = Readonly<{
  name?: string;
  stabilityClass?: StabilityClass;
  defaultDestinationAccountId?: string | null;
  isActive?: boolean;
}>;

export async function updateIncomeSource(
  incomeSourceId: string,
  patch: IncomeSourcePatch,
): Promise<void> {
  const { error } = await supabase
    .from('income_sources')
    .update({
      ...(patch.name === undefined ? {} : { name: patch.name.trim() }),
      ...(patch.stabilityClass === undefined ? {} : { stability_class: patch.stabilityClass }),
      ...(patch.defaultDestinationAccountId === undefined
        ? {}
        : { default_destination_account_id: patch.defaultDestinationAccountId }),
      ...(patch.isActive === undefined ? {} : { is_active: patch.isActive }),
    })
    .eq('id', incomeSourceId);
  if (error) throw appErrorException(error);
}
